import React, { useState } from "react";
import {
  formatCurrency,
  formatPercentWithSignAndPercentage,
} from "../utils/numberFormatters";
import { SummaryTableAccordionProps } from "../types/componentTypes";

const SummaryTableAccordion: React.FC<SummaryTableAccordionProps> = ({
  headerTitle,
  estimated,
  estimatedTitle = "Estimated",
  actual,
  difference,
  calculatedData,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Accordion Header */}
      <div
        className="flex flex-col items-center gap-4 my-1 bg-white rounded-lg cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex w-full flex-row">
          <div className="flex-[6]">
            <table className="table-auto w-full">
              <thead>
                <tr>
                  <th className="p-2 h-12 text-left text-black text-lg">
                    {headerTitle}
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          <div className="flex-[4]">
            <table className="table-auto w-full">
              <thead>
                <tr className="flex min-w-0">
                  <th
                    className={`flex-[3] p-2 h-12 flex items-center align-end justify-end ${
                      estimatedTitle === "Forecast"
                        ? "bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple"
                        : "bg-[#6D53FD]"
                    } text-white text-lg`}
                  >
                    {formatCurrency(
                      typeof estimated === "function" ? estimated() : estimated
                    )}
                  </th>
                  <th className="flex-[3] p-2 h-12 flex items-center align-end justify-end bg-[#19AEDE] text-white text-lg">
                    {formatCurrency(
                      typeof actual === "function" ? actual() : actual
                    )}
                  </th>
                  <th className="flex-[3] p-2 h-12 text-left text-lg text-center">
                    {typeof difference === "function"
                      ? formatPercentWithSignAndPercentage(difference())
                      : formatPercentWithSignAndPercentage(difference)}
                  </th>
                  <th className="flex-[1] p-2 h-12">{isOpen ? "▲" : "▼"}</th>
                </tr>
              </thead>
            </table>
          </div>
        </div>
      </div>

      {/* SubAccordion (expanded) Content */}
      {isOpen && (
        <div className="flex min-w-0 flex-row">
          <div className="flex-[6]">
            <table className="table-auto w-full">
              <tbody>
                {calculatedData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="p-2 h-12">{row.subSectionTitle}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex-[4]">
            <table className="table-auto w-full">
              <tbody>
                {calculatedData.map((row, rowIndex) => (
                  <tr key={rowIndex} className="flex min-w-0 text-center">
                    <td
                      className={`flex-[3] p-2 h-12 ${
                        estimatedTitle === "Forecast"
                          ? "bg-gradient-to-r from-[#BCE5F8] to-[#E0C5FF]"
                          : "bg-[#F1E5FF]"
                      } text-right`}
                    >
                      <div className="bg-linear-to-r from-purple-500 to-blue-500">
                        {row.invertColors ? "-" : ""}
                        {formatCurrency(
                          typeof row.estimated === "function"
                            ? row.estimated()
                            : row.estimated
                        )}
                      </div>
                    </td>
                    <td className="flex-[3] p-2 h-12 bg-[#E2F4FC] text-right">
                      {row.invertColors ? "-" : ""}
                      {formatCurrency(
                        typeof row.actual === "function"
                          ? row.actual()
                          : row.actual
                      )}
                    </td>
                    <td className="flex-[3] p-2 h-12 bg-gray-100">
                      {row.difference === 0 ? (
                        <span className="text-black">0%</span>
                      ) : (
                        <span
                          className={
                            (typeof row.difference === "function"
                              ? row.difference()
                              : row.difference) > 0
                              ? row.invertColors
                                ? "text-red-600"
                                : "text-green-600"
                              : row.invertColors
                              ? "text-green-600"
                              : "text-red-600"
                          }
                        >
                          {formatPercentWithSignAndPercentage(
                            typeof row.difference === "function"
                              ? row.difference()
                              : row.difference
                          )}
                        </span>
                      )}
                    </td>

                    <td className="flex-[1] p-2 h-12 bg-gray-100"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default SummaryTableAccordion;
